/* OnSumn — RulesPopover
 *
 * In-page Liquid Glass venue-rules sheet that MORPHS out of the ENTRY chip
 * via the FLIP technique. Sibling of MerchPopover — the skeleton (FLIP
 * open/close, portal, Esc/backdrop close, scroll lock, focus management,
 * reduced-motion fade) is copied from components/MerchPopover.jsx VERBATIM;
 * everything intentionally different lives in the spec's deviations ledger
 * (docs/design/2026-08-19-venue-rules-popover-spec.md §2a):
 *
 *   panel width 640 (not 720) · fog accent (not amber) · stamp tilt +1.2deg
 *   (mirrored) · dialog aria-label, no aria-labelledby · no terminal brick in ok
 *   state (fog brick in drift only) · keyframes id rules-popover-keyframes
 *
 * MERCH is amber commerce ending in a conversion brick; RULES is fog-blue
 * civic signage ending in a citation. Orange appears exactly once — the
 * headline's terminal period (static signature, not an affordance).
 *
 * DATA DISCIPLINE (Sable's contract, Atlas's ruling)
 * ──────────────────────────────────────────────────
 * - The entry + state arrive via props from the chip, which computed them
 *   through lib/venue-policies-gate.js — the ONE client gate (lockstep-
 *   guarded against scrapers/venue-policies.js#isRenderable).
 * - essentials / prohibited / allowedItems render PRE-BAKED from data.
 *   Never re-template, never re-extract numbers from strings. The hero's
 *   aria long form comes from bag.maxDims.{w,h,d} first-class numbers; the
 *   visual comes from the pre-baked maxDims.display string (split on glyphs
 *   for two-tone color — rendered verbatim, in order, nothing recomputed).
 * - verified_at is a "YYYY-MM-DD" string formatted by SPLITTING THE STRING
 *   (lib/venue-policies-gate.js#formatPolicyDate). new Date('YYYY-MM-DD')
 *   parses UTC midnight and renders a day early in Pacific.
 * - drift-pointer state suppresses hero, entry facts, prohibited, allowed
 *   and essentials ENTIRELY — we don't show numbers we can't back. The
 *   caution plate + fog brick hand the reader the venue's page.
 * - Event-level rules (seasonal lane, 2026-10-01; Zora spec
 *   docs/design/2026-10-01-seasonal-rail-spec.md §3): the row's own cited
 *   rules arrive as the `eventRules` prop on both chip paths (venue match
 *   with the row's parking riding along, or the synthetic entry built from
 *   the row). WHICH groups render — PARKING first, its own cite row only
 *   when its source differs from the footer's, BAGS text vs plate vs the
 *   no-bag line vs nothing — is decided by lib/venue-policies-gate.js#
 *   eventRulesGroups, the one mapper the /e/ SSR panel also reads. This
 *   component only draws it. On a synthetic entry (groups.synthetic —
 *   source_kind 'event') a park or a street has no venue, so every string
 *   that names one takes its event variant, chosen by a ternary at the
 *   string site: the event's title or the organizer's page, never "venue",
 *   never "official" (copy deck docs/copy/2026-10-01-seasonal-copy-deck.md
 *   §7). Venue-path strings are unchanged. The drift-only strings have no
 *   variant: a synthetic entry is always status 'ok' (eventRulesEntry).
 *
 * COPY: docs/design/2026-08-19-venue-rules-copy-deck.md — character-exact,
 * including the Atlas wording bans (no "guaranteed", no "official" about OUR
 * summary, no "up to date", no door-gaming advice).
 *
 * A11Y (Iris co-sign, spec §4)
 * ────────────────────────────
 * - role="dialog" + aria-modal + explicit aria-label = the ENTRY chip's string
 *   (`rulesChipAria`); NO aria-labelledby — labelledby outranks label, so the
 *   h1 would be the name (2026-10-02). h1 keeps id for the tests.
 * - Prohibited/allowed are real <ul role="list"> (explicit role — list-style:
 *   none strips semantics in Safari/VO); entry facts are one <dl>
 * - Decorative squares / ↳ / ↗ / rules aria-hidden
 * - Esc + backdrop close; focus → close ≤250ms; restore to trigger on close
 * - Hard Tab/Shift-Tab focus trap while open (lib/focus-trap.js, shared
 *   with MerchPopover — the 2026-08-20 parity pass closing the "shared
 *   skeleton gap" the spec §4.6 logged as follow-up debt)
 * - prefers-reduced-motion → skip morph AND the stamp-settle, fade only
 */

import {
  formatPolicyDate,
  policyHost,
  entryFactGroups,
  eventRulesGroups,
  AMBER_CAUTION,
} from '../lib/venue-policies-gate.js';
import { trapDialogTab } from '../lib/focus-trap.js';

// entryFactGroups (the AT THE DOOR fact-token mapper) lives in
// lib/venue-policies-gate.js since 2026-08-20 — the /e/ share page renders
// the same facts server-side, and the two surfaces must share ONE mapper.

function RulesPopover({ event, entry, state = 'ok', darkMode, triggerRect, onClose, eventRules = null }) {
  const panelRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);
  const previouslyFocusedRef = React.useRef(null);
  // Closing guard as a REF, not state — one deliberate fix over the copied
  // MerchPopover skeleton. The Esc listener below is attached in a []-dep
  // effect, so it captures the FIRST render's handleClose; a useState guard
  // is a stale `false` inside that closure forever, and a double-Esc fires
  // onClose twice (tests/venue-rules-popover.test.js pins this). A ref reads
  // live through any stale closure, so the guard actually guards. (Merch
  // carried the same latent double-fire — ported back 2026-08-20 in the
  // popover a11y parity pass, alongside the shared focus trap.)
  const closingRef = React.useRef(false);

  const reducedMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── FLIP morph (open) — MerchPopover verbatim ───
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (reducedMotion || !triggerRect) {
      panel.style.transition = 'opacity 200ms ease';
      panel.style.opacity = '0';
      panel.getBoundingClientRect();
      requestAnimationFrame(() => { panel.style.opacity = '1'; });
      return;
    }

    const toRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - toRect.left;
    const dy = triggerRect.top - toRect.top;
    const sx = Math.max(0.05, triggerRect.width / toRect.width);
    const sy = Math.max(0.05, triggerRect.height / toRect.height);

    panel.style.transformOrigin = 'top left';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0.85';
    panel.getBoundingClientRect();
    requestAnimationFrame(() => {
      panel.style.transition =
        'transform 420ms cubic-bezier(0.16, 1, 0.3, 1), ' +
        'opacity 240ms ease 60ms';
      panel.style.transform = 'translate(0, 0) scale(1, 1)';
      panel.style.opacity = '1';
    });
  }, [triggerRect, reducedMotion]);

  // ─── Esc + focus trap + body scroll lock + focus management —
  // MerchPopover verbatim (the trap joined BOTH skeletons 2026-08-20) ───
  React.useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    // Escape closes from anywhere; Tab/Shift-Tab hard-cycle inside the
    // dialog (lib/focus-trap.js — panelRef is read at keydown time, so
    // this []-dep closure can't go stale). Keyboard-only: pointer
    // interactions never route through here, so mouse focus is untouched.
    const onKey = (e) => {
      if (e.key === 'Escape') { handleClose(); return; }
      trapDialogTab(panelRef.current, e);
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 200);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        try { previouslyFocusedRef.current.focus(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── FLIP morph (close) — MerchPopover verbatim except the ref guard ───
  const handleClose = React.useCallback(() => {
    if (closingRef.current) return;
    const panel = panelRef.current;
    if (!panel || reducedMotion || !triggerRect) {
      closingRef.current = true;
      onClose();
      return;
    }
    closingRef.current = true;

    const fromRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - fromRect.left;
    const dy = triggerRect.top - fromRect.top;
    const sx = Math.max(0.05, triggerRect.width / fromRect.width);
    const sy = Math.max(0.05, triggerRect.height / fromRect.height);

    panel.style.transition =
      'transform 320ms cubic-bezier(0.4, 0, 1, 1), opacity 200ms ease 80ms';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';

    setTimeout(() => onClose(), 320);
  }, [onClose, reducedMotion, triggerRect]);

  if (!event || !entry) return null;
  if (state !== 'ok' && state !== 'stale-badge' && state !== 'drift-pointer') return null;

  const drift = state === 'drift-pointer';
  const stale = state === 'stale-badge';
  const policy = entry.policy || {};
  const bag = policy.bag || {};
  const rules = policy.entry || {};
  const venue = entry.venue || event.venue || '';
  const city = (event && (event.city || event.tag)) || '';
  const host = policyHost(entry.source_url);
  const verifiedDate = formatPolicyDate(entry.verified_at);
  // Event-level groups (see the header): PARKING + the bag-slot decision.
  // drift-pointer → every group null; the drift branch never reads them.
  const groups = eventRulesGroups({ entry, state, eventRules });
  const parking = groups.parking;
  // What a synthetic entry's strings name instead of a venue.
  const eventName = event.title || venue;

  // ─── Theme tokens — surface family identical to MerchPopover; accent
  // channel is FOG (spec §0). amberCaution is the drift tell only. ───
  const surface = darkMode ? '#121414' : '#F8F8F8';
  const onSurface = darkMode ? '#E2E2E2' : '#1A1A1A';
  const muted = darkMode ? 'rgba(226, 226, 226, 0.58)' : 'rgba(26, 26, 26, 0.62)';
  const fogReadable = darkMode ? '#A3BDCC' : 'oklch(0.43 0.046 242)';
  const fogRule = darkMode ? 'rgba(122,153,172,0.55)' : 'rgba(74,102,120,0.6)';
  const amberCaution = darkMode ? AMBER_CAUTION.dark : AMBER_CAUTION.light;
  const brickBg = darkMode ? '#7A99AC' : '#4A6678';
  const brickInk = darkMode ? '#0C0F0F' : '#FFF';
  const brickShadow = darkMode ? '#0a0a0a' : 'oklch(0.35 0.045 237)';

  const panelGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'panel')
    : { background: surface };

  const btnGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'btn')
    : {};

  const stop = (e) => e.stopPropagation();

  // Fog hairline between sections (spec §2b).
  const sectionRule = (
    <hr aria-hidden="true" style={{
      border: 0, height: 1, background: fogRule,
      margin: 'clamp(24px, 3vw, 36px) 0',
    }} />
  );

  const sectionKicker = (text) => (
    <p style={{
      margin: '0 0 14px',
      fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
      letterSpacing: '0.2em', textTransform: 'uppercase',
      color: fogReadable, lineHeight: 1.3,
    }}>{text}</p>
  );

  const tickSquare = (size, color) => (
    <span aria-hidden="true" style={{
      width: size, height: size, flex: '0 0 auto',
      background: color,
    }} />
  );

  // ─── Hero plate (THE BAG RULE) content resolution ───
  // Word variants outrank the mono dims lockup; the modifier line is the
  // PRE-BAKED essentials bag line (always essentials[0] when a bag rule
  // exists, never cut — scrapers/venue-policies.js#synthesizeEssentials),
  // because Rhea's deck defines no separate modifier strings and we never
  // mint copy client-side.
  const essentials = Array.isArray(policy.essentials) ? policy.essentials : [];
  let heroWord = null;
  let heroModifier = null;
  if (bag.clearRequired === true) {
    heroWord = 'CLEAR BAG ONLY';
    heroModifier = essentials[0] || null;
  } else if (bag.noBags === true) {
    heroWord = 'NO BAGS';
    heroModifier = bag.clutchDims ? (essentials[0] || null) : null;
  }
  const heroDims = !heroWord && bag.maxDims ? bag.maxDims : null;
  const heroDimsAria = heroDims
    ? `Bag size limit: ${heroDims.w} by ${heroDims.h}${heroDims.d != null ? ` by ${heroDims.d}` : ''} inches`
    : null;
  // Two-tone the pre-baked display string: glyphs (″ ×) fog, numerals
  // onSurface. Split KEEPS every segment in order — the string renders
  // verbatim; nothing is re-extracted or recomputed.
  const heroDimsTokens = heroDims && typeof heroDims.display === 'string'
    ? heroDims.display.split(/([″×])/)
    : null;

  const prohibited = Array.isArray(policy.prohibited) ? policy.prohibited : [];
  const allowed = Array.isArray(policy.allowedItems) ? policy.allowedItems : [];
  const facts = entryFactGroups(rules);

  // One hairline BETWEEN groups (the byline rule above opens the body). A
  // venue entry always fills the bag slot, so for venues every flag below
  // is true and the markup is unchanged; a synthetic entry can open on
  // LEAVE AT HOME with nothing above it, and must not draw a doubled rule.
  const aboveFacts = Boolean(parking) || groups.bagSlot !== null;
  const aboveProhibited = aboveFacts || facts.length > 0;
  const aboveEssentials = aboveProhibited || prohibited.length > 0 || allowed.length > 0;

  // A park is not something you go "inside" (Zora §3): on a synthetic entry
  // the list is named for the event the reader is going to.
  const prohibitedAria = groups.synthetic
    ? `Items not allowed at ${(event && event.title) || venue}`
    : `Items not allowed inside ${venue}`;

  // Prose groups (PARKING, BAGS) — the no-bag line's 14/1.5 body type in
  // onSurface (Zora §3: a parking note runs two or three lines).
  const groupProse = (text) => (
    <p style={{
      margin: 0,
      fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
      lineHeight: 1.5, color: onSurface,
    }}>{text}</p>
  );

  // Footer-link underline behavior, for the PARKING cite link (dotted at
  // rest → solid on hover/focus, the footer citation verbatim).
  const underlineSolid = (e) => { e.currentTarget.style.textDecoration = 'underline solid 1px'; };
  const underlineDotted = (e) => { e.currentTarget.style.textDecoration = 'underline dotted 1px'; };

  // Real <ul role="list"> — list-style:none strips list semantics in
  // Safari/VoiceOver without the explicit role (Iris §4.3). Cap 8; a longer
  // list appends the non-squared FULL LIST li → source_url.
  const itemsList = (kicker, items, ariaLabel) => (
    <div>
      {sectionKicker(kicker)}
      <ul role="list" aria-label={ariaLabel} style={{
        listStyle: 'none', padding: 0, margin: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '10px 20px',
      }}>
        {items.slice(0, 8).map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            {tickSquare(5, fogReadable)}
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
              lineHeight: 1.4, color: onSurface,
            }}>{it}</span>
          </li>
        ))}
        {items.length > 8 && (
          <li style={{ display: 'flex', alignItems: 'baseline' }}>
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              style={{
                fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: fogReadable,
                textDecoration: 'underline dotted 1px',
                textUnderlineOffset: 3,
              }}
            >{groups.synthetic ? "FULL LIST ON THE ORGANIZER'S PAGE" : "FULL LIST ON THE VENUE'S PAGE"} <span aria-hidden="true">↗</span></a>
          </li>
        )}
      </ul>
    </div>
  );

  const overlay = (
    <div
      role="presentation"
      onClick={(e) => { stop(e); handleClose(); }}
      style={{
        // z=2000 — same layer as MerchPopover (above Spotlight search
        // surfaces, below FilterPopup). The two popovers can't race: each
        // opens from its own chip gesture.
        position: 'fixed', inset: 0, zIndex: 2000,
        background: darkMode ? 'rgba(8, 8, 10, 0.55)' : 'rgba(40, 30, 20, 0.45)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(12px, 4vw, 32px)',
        animation: reducedMotion ? 'none' : 'rules-backdrop-in 220ms ease both',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={groups.synthetic ? `Entry rules for ${eventName}` : `Entry rules and bag policy at ${venue}`}
        onClick={stop}
        style={{
          ...panelGlass,
          // Same light-mode near-opaque override as MerchPopover — the
          // layered glass white reads thin over busy event grids.
          ...(!darkMode && { background: 'rgba(255, 255, 255, 0.96)' }),
          border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
          borderRadius: 24,
          width: 'min(640px, 100%)',
          maxHeight: '92vh', overflowY: 'auto',
          padding: 'clamp(20px, 3vw, 28px)',
          position: 'relative',
          willChange: 'transform, opacity',
          boxShadow: (panelGlass.boxShadow || '') +
            ', 0 30px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04)',
        }}
      >
        {/* Top bar — issue stamp (mirrored tilt: +1.2deg, merch is −1.2) + close */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, marginBottom: 'clamp(20px, 3vw, 32px)',
        }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: drift ? amberCaution : fogReadable,
            padding: '5px 10px',
            border: `1.5px solid ${drift ? amberCaution : fogReadable}`,
            borderRadius: 2,
            transform: 'rotate(1.2deg)', transformOrigin: 'left center',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 56px)',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>ISSUE №02 · ENTRY</span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={handleClose}
            aria-label={groups.synthetic ? 'Close event rules' : 'Close venue rules'}
            style={{
              ...btnGlass,
              width: 44, height: 44, borderRadius: '50%',
              border: darkMode ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.1)',
              color: onSurface,
              fontFamily: 'var(--font-display)',
              fontSize: 20, fontWeight: 700, lineHeight: 1, padding: 0,
              cursor: 'pointer', flex: '0 0 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: reducedMotion ? 'none' : 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onMouseEnter={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(90deg)'; }}
            onMouseLeave={(e) => { if (!reducedMotion) e.currentTarget.style.transform = 'rotate(0deg)'; }}
          >×</button>
        </div>

        {/* Kicker — no count tail; trailing rule stays fog in every state
            (cautious, not alarmed) */}
        <p style={{
          margin: '0 0 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: fogReadable, lineHeight: 1.3,
        }}>
          {groups.synthetic ? `Ground rules · ${eventName}` : `House rules · ${venue}`}
          <span aria-hidden="true" style={{
            flex: '1 1 auto', height: 1, maxWidth: 180, minWidth: 24,
            background: fogRule,
          }} />
        </p>

        {/* Display headline — orange appears exactly once, here */}
        <h1 id="rules-headline" style={{
          fontFamily: 'var(--font-display)', fontWeight: 900,
          fontSize: 'clamp(36px, 9vw, 72px)', lineHeight: 0.94,
          letterSpacing: '-0.025em',
          color: onSurface, margin: 0,
          textTransform: 'uppercase',
          textWrap: 'balance',
        }}>
          WALK IN<br />
          LIKE YOU KNOW<span style={{ color: 'var(--international-orange)' }}>.</span>
        </h1>

        {/* Drift caution plate — in-flow DIRECTLY after the headline so a
            screen reader hits the caution before anything else (Iris §4.4;
            no live region). */}
        {drift && (
          <div style={{
            marginTop: 20,
            border: `1.5px solid ${amberCaution}`,
            borderRadius: 2,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <p style={{
              margin: 0, display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: amberCaution, lineHeight: 1.3,
            }}>
              {tickSquare(6, amberCaution)}
              THE RULES MOVED.
            </p>
            <p style={{
              margin: 0,
              fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
              lineHeight: 1.45, color: onSurface,
            }}>
              {venue} changed their policy page since our last check. We don't show numbers we can't back — get it from the source.
            </p>
          </div>
        )}

        {/* Byline */}
        <p style={{
          margin: '16px 0 0',
          display: 'flex', gap: 8, alignItems: 'baseline',
          fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500,
          color: muted, lineHeight: 1.45,
        }}>
          <span aria-hidden="true" style={{ color: fogReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
          <span>
            <strong style={{ color: onSurface, fontWeight: 700 }}>{venue}</strong>
            {city ? ` · ${city}` : ''}
          </span>
        </p>

        {sectionRule}

        {drift ? (
          /* Drift: the popover's ONLY brick CTA — fog, not orange. The
             venue's page is the truth; every number stays down. */
          <a
            href={entry.source_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            aria-label={`Read ${venue}'s official rules, opens in new tab`}
            style={{
              display: 'block', textAlign: 'center', textDecoration: 'none',
              fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              padding: '16px 20px',
              background: brickBg,
              color: brickInk, border: 0, borderRadius: 0,
              boxShadow: `6px 6px 0 0 ${brickShadow}`,
              transition: reducedMotion ? 'none' : 'transform 160ms ease, box-shadow 160ms ease',
            }}
            onMouseEnter={(e) => {
              if (reducedMotion) return;
              e.currentTarget.style.transform = 'translate(2px, 2px)';
              e.currentTarget.style.boxShadow = `4px 4px 0 0 ${brickShadow}`;
            }}
            onMouseLeave={(e) => {
              if (reducedMotion) return;
              e.currentTarget.style.transform = 'translate(0, 0)';
              e.currentTarget.style.boxShadow = `6px 6px 0 0 ${brickShadow}`;
            }}
          >
            Get the current rules <span aria-hidden="true">↗</span>
            {host && (
              <span style={{
                display: 'inline-block',
                marginLeft: 8, paddingLeft: 8,
                borderLeft: `1px solid ${darkMode ? 'rgba(12,15,15,0.35)' : 'rgba(255,255,255,0.35)'}`,
                fontWeight: 700,
                color: darkMode ? 'rgba(12,15,15,0.85)' : 'rgba(255,255,255,0.85)',
                letterSpacing: '0.08em',
              }}>{host}</span>
            )}
          </a>
        ) : (
          <React.Fragment>
            {/* PARKING — the row's own claim, FIRST among groups (Zora §3).
                Its cite row renders only when the claim's source differs
                from the footer's (the venue path: the footer cites the
                VENUE's page, this sentence is the organizer's). On the
                synthetic path the footer already cites the same page and
                date, and a second stamp would only repeat it. */}
            {parking && (
              <div>
                {sectionKicker('Parking')}
                {groupProse(parking.text)}
                {parking.cite && (
                  <div style={{
                    marginTop: 10, minHeight: 44,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 12,
                  }}>
                    {/* The footer stamp verbatim: nowrap, clips from the
                        END, minWidth:0 down the shrink chain; the link is
                        the rigid side (Jace 390px rule). */}
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      minWidth: 0, flex: '1 1 auto',
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
                      textTransform: 'uppercase', color: muted,
                    }}>
                      {tickSquare(5, fogReadable)}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                        VERIFIED {formatPolicyDate(parking.cite.verifiedAt)}{parking.cite.host ? ` · ${parking.cite.host}` : ''}
                      </span>
                    </span>
                    <a
                      href={parking.cite.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={stop}
                      aria-label={`Read the parking source on ${parking.cite.host}, opens in new tab`}
                      onMouseEnter={underlineSolid}
                      onMouseLeave={underlineDotted}
                      onFocus={underlineSolid}
                      onBlur={underlineDotted}
                      style={{
                        fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: fogReadable, flex: '0 0 auto',
                        textDecoration: 'underline dotted 1px',
                        textUnderlineOffset: 3,
                      }}
                    >SOURCE <span aria-hidden="true">↗</span></a>
                  </div>
                )}
              </div>
            )}

            {/* Bag slot (lib eventRulesGroups#bagSlot). 'plate' — THE BAG
                RULE: sharp corners, fog frame, mono numerals. 'no-bag-line'
                — a VENUE with other facts but no bag rule (copy deck §8 —
                unposted is not unenforced). 'bags' — a synthetic entry's
                bag sentence as plain prose: plate type would claim a
                precision a paraphrase does not have. null — a synthetic
                entry without bag text: nothing at all (a park has no policy
                page, so the no-bag sentence would be false). */}
            {groups.bagSlot && parking && sectionRule}
            {groups.bagSlot === 'plate' ? (
              <div style={{
                border: `1.5px solid ${fogReadable}`,
                borderRadius: 2,
                padding: '16px 18px',
                display: 'flex', flexDirection: 'column', gap: 6,
                background: 'transparent',
              }}>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: fogReadable,
                }}>THE BAG RULE</span>
                {heroWord ? (
                  <span style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(22px, 5vw, 32px)', fontWeight: 900,
                    textTransform: 'uppercase', lineHeight: 1.1,
                    color: onSurface,
                  }}>
                    {heroWord}<span style={{ color: 'var(--international-orange)' }}>.</span>
                  </span>
                ) : heroDimsTokens ? (
                  <span role="img" aria-label={heroDimsAria}>
                    <span aria-hidden="true" style={{
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      fontSize: 'clamp(26px, 5.5vw, 40px)', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
                      color: onSurface,
                    }}>
                      {heroDimsTokens.map((tok, i) => (
                        (tok === '″' || tok === '×')
                          ? <span key={i} style={{ color: fogReadable }}>{tok}</span>
                          : tok
                      ))}
                    </span>
                  </span>
                ) : null}
                {heroModifier && (
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                    color: muted, lineHeight: 1.4,
                  }}>{heroModifier}</span>
                )}
              </div>
            ) : groups.bagSlot === 'bags' ? (
              <div>
                {sectionKicker('Bags')}
                {groupProse(groups.bagText)}
              </div>
            ) : groups.bagSlot === 'no-bag-line' ? (
              <p style={{
                margin: 0,
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                lineHeight: 1.5, color: muted,
              }}>
                {venue} hasn't posted a bag policy — when in doubt, pack light. Here's what we do know.
              </p>
            ) : null}

            {/* Entry facts — AT THE DOOR, one <dl> grid */}
            {facts.length > 0 && (
              <React.Fragment>
                {aboveFacts && sectionRule}
                {sectionKicker('AT THE DOOR')}
                <dl style={{
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
                  gap: '16px 24px',
                }}>
                  {facts.map((f) => (
                    <div key={f.label}>
                      <dt style={{
                        fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
                        letterSpacing: '0.14em', textTransform: 'uppercase',
                        color: muted, margin: '0 0 4px',
                      }}>{f.label}</dt>
                      <dd style={{
                        fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                        textTransform: 'uppercase', color: onSurface, margin: 0,
                      }}>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </React.Fragment>
            )}

            {/* Prohibited — LEAVE AT HOME. Sentence case, no red, no ✕, no
                warning icons: the item is the subject, never the user. */}
            {prohibited.length > 0 && (
              <React.Fragment>
                {aboveProhibited && sectionRule}
                {itemsList('LEAVE AT HOME', prohibited, prohibitedAria)}
              </React.Fragment>
            )}

            {/* Allowed — STILL GOOD, same anatomy, directly after */}
            {allowed.length > 0 && (
              <React.Fragment>
                <div style={{ height: 20 }} aria-hidden="true" />
                {itemsList('STILL GOOD', allowed, `Items allowed inside ${venue}`)}
              </React.Fragment>
            )}

            {/* Essentials — pre-baked strings from data, never re-templated */}
            {essentials.length > 0 && (
              <React.Fragment>
                {aboveEssentials && sectionRule}
                {sectionKicker('THE ESSENTIALS')}
                <div>
                  {essentials.map((line, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: 8, alignItems: 'baseline',
                      marginBottom: i === essentials.length - 1 ? 0 : 10,
                    }}>
                      <span aria-hidden="true" style={{ color: fogReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
                      <span style={{
                        fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                        lineHeight: 1.5, color: onSurface,
                      }}>{line}</span>
                    </div>
                  ))}
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        {sectionRule}

        {/* Verified footer — the citation. Stamp-settle micro-moment on ok +
            stale only (spec §2c); drift and reduced-motion render static. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, minHeight: 44,
          animation: (!drift && !reducedMotion)
            ? 'rules-stamp-settle 240ms cubic-bezier(0.16, 1, 0.3, 1) 460ms both'
            : 'none',
        }}>
          {/* Mono stamps never wrap (Jace 390px finding: the stamp broke
              mid-date to three lines). The ISSUE stamp's nowrap+ellipsis
              treatment, adapted for flex anatomy: text-overflow can't
              ellipsize a flex container's anonymous text child, so the text
              sits in an inner span and minWidth:0 runs down the shrink
              chain. The stamp is the flexible side of the footer row; the
              string clips from its END, so the host gives way before the
              date ever breaks. */}
          {drift ? (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 8,
              minWidth: 0, flex: '1 1 auto',
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: amberCaution,
            }}>
              {tickSquare(5, amberCaution)}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                LAST VERIFIED {verifiedDate} — RE-CHECKING
              </span>
            </span>
          ) : stale ? (
            <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: '1 1 auto' }}>
              <span style={{
                display: 'flex', alignItems: 'center', gap: 8,
                minWidth: 0,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: amberCaution,
              }}>
                {tickSquare(5, amberCaution)}
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                  LAST CHECKED {verifiedDate}
                </span>
              </span>
              {/* Supporting line is body copy — it wraps, only the stamp is
                  nowrap. */}
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
                color: muted, lineHeight: 1.4,
              }}>{groups.synthetic ? "It's been a minute — double-check the organizer's page." : "It's been a minute — double-check the venue's page."}</span>
            </span>
          ) : (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 8,
              minWidth: 0, flex: '1 1 auto',
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: muted,
            }}>
              {tickSquare(5, fogReadable)}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                VERIFIED {verifiedDate}{host ? ` · ${host}` : ''}
              </span>
            </span>
          )}
          {/* Quiet citation link — ok + stale. In drift the brick above is
              the one and only source affordance (tab order: close → brick).
              Label is the copy deck §6 sanctioned short form at ALL widths
              (Jace 390px finding: the long form's letter-spacing advance
              overflowed the panel ~3px) — the aria-label keeps the full
              "Read {venue}'s official rules" text, so SR users lose
              nothing. The link is the rigid side; the stamp truncates. */}
          {!drift && (
            <a
              href={entry.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              aria-label={groups.synthetic ? `Read the organizer's page for ${eventName}, opens in new tab` : `Read ${venue}'s official rules, opens in new tab`}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline solid 1px'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'underline dotted 1px'; }}
              onFocus={(e) => { e.currentTarget.style.textDecoration = 'underline solid 1px'; }}
              onBlur={(e) => { e.currentTarget.style.textDecoration = 'underline dotted 1px'; }}
              style={{
                fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: fogReadable, flex: '0 0 auto',
                textDecoration: 'underline dotted 1px',
                textUnderlineOffset: 3,
              }}
            >{groups.synthetic ? "ORGANIZER'S PAGE" : 'VENUE POLICY'} <span aria-hidden="true">↗</span></a>
          )}
        </div>

        {/* Disclaimer — Atlas four-load floor; always rendered, all states */}
        <p style={{
          margin: '16px 0 0',
          display: 'flex', gap: 8,
          fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
          color: muted, lineHeight: 1.55, letterSpacing: '0.01em',
        }}>
          <span aria-hidden="true" style={{ color: fogReadable, fontWeight: 700, flex: '0 0 auto' }}>↳</span>
          {groups.synthetic
            ? "Rules change, sometimes day of — the organizer's page is the final word, so check it before you roll. We're the heads-up, not the organizer."
            : "Rules change, and one show can flip them — the venue's page is the final word, so check it before you roll. We're the heads-up, not the house."}
        </p>
      </div>
    </div>
  );

  // Portal escape — same reasoning as MerchPopover: EventCard's hover
  // transform + overflow:hidden would trap position:fixed to the card.
  if (typeof window !== 'undefined'
      && window.ReactDOM
      && typeof window.ReactDOM.createPortal === 'function'
      && typeof document !== 'undefined') {
    return window.ReactDOM.createPortal(overlay, document.body);
  }
  return overlay;
}

// Backdrop fade + stamp-settle keyframes — injected once on first load,
// under the sibling's OWN id (deviations ledger).
if (typeof document !== 'undefined' && !document.getElementById('rules-popover-keyframes')) {
  const style = document.createElement('style');
  style.id = 'rules-popover-keyframes';
  style.textContent =
    '@keyframes rules-backdrop-in { from { opacity: 0; } to { opacity: 1; } }\n' +
    '@keyframes rules-stamp-settle { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }';
  document.head.appendChild(style);
}

window.RulesPopover = RulesPopover;
